/**
 * The audit log — *"a per-org audit log that people can use to assess what's going on"*.
 *
 * U2's ruling in one screen, and the ruling's other half is what this screen deliberately
 * is **not**: there is no cross-tenant view, no staff identity, and no way to reach another
 * organization's rows from here. It is an ordinary org-scoped read on the dense table c24
 * built, and keeping it ordinary is the point — the rejected reading of that sentence was
 * the largest new security surface the product could have acquired.
 *
 * ## No new components, and one interaction
 *
 * The re-plan says *"a minimal view on the dense table from c24: actor, action, entity,
 * timestamp, and the before/after diff on a row. No new components."* The diff is the one
 * thing that needs a control, because rendering every snapshot inline would put a wall of
 * JSON between the reader and the trail they came to read.
 *
 * **So {@link AuditDiff} is exported, and that is c35's rule rather than a convenience.**
 * The expanded state is `useState` moved only by a click; `renderToStaticMarkup` attaches
 * no reconciler, so a test that could only reach this screen through its default render
 * would never see a diff at all — and the test written anyway would be a hand-copy of this
 * JSX that stays green while the real component changes underneath it. That happened three
 * times in c35 and once in c34 before anyone noticed the shape.
 *
 * ## Why there is no filter bar
 *
 * `GET /v1/audit-log` takes a cursor and a limit and nothing else, and the reason is in
 * `packages/shared/src/api/audit-log.ts`: a filter would be this commit inventing spec
 * surface, and the day the list needs one is the same conversation as the **gate** §5 still
 * carries. Two decisions about one route, better made together in Phase 5 than one now.
 */
import { useCallback, useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";

import {
  DataTable,
  INITIAL_CURSOR,
  Surface,
  type Column,
  type TableCursor,
} from "@packagepage/ui";
import { DEFAULT_AUDIT_PAGE_SIZE, type AuditLogEntry } from "@packagepage/shared";

import { api } from "../api/procedures.js";
import {
  actorLabel,
  auditTimestamp,
  describeAction,
  diffFields,
  type DiffField,
} from "./audit-log.js";

// Every screen imports the stylesheets it uses — a screen that renders correctly only
// because a parent happened to import its CSS breaks the day the parent stops needing it.
import "@packagepage/ui/table.css";
import "@packagepage/ui/surface.css";
import "./audit-log.css";

/**
 * One row's before/after, as its own component so a static render can reach it.
 *
 * Exported for the reason in this file's header. It takes the two snapshots rather than an
 * entry, so a test can drive the cases that matter — a create (no `before`), a delete (no
 * `after`), an update where one field moved, and a snapshot that is not an object — without
 * constructing a whole log row for each.
 */
export function AuditDiff({ before, after }: { before: unknown; after: unknown }) {
  const fields: DiffField[] = diffFields(before, after);

  if (fields.length === 0) {
    // Both snapshots absent. Says so, rather than rendering an empty box the reader has to
    // interpret — the row is still evidence that the action happened.
    return (
      <p className="pp-audit-diff-empty">This action recorded no before or after.</p>
    );
  }

  return (
    <table className="pp-audit-diff">
      <thead>
        <tr>
          <th scope="col">Field</th>
          <th scope="col">Before</th>
          <th scope="col">After</th>
        </tr>
      </thead>
      <tbody>
        {fields.map((field) => (
          <tr
            key={field.key}
            // Marked in the DOM rather than only in colour: "what actually moved" is the
            // question this table exists to answer, and a reader who cannot see the
            // highlight still needs it.
            data-changed={field.changed ? "true" : "false"}
          >
            <th scope="row">{field.key === "" ? "value" : field.key}</th>
            <td>{field.before ?? <span className="pp-audit-absent">absent</span>}</td>
            <td>{field.after ?? <span className="pp-audit-absent">absent</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface Row {
  id: string;
  entry: AuditLogEntry;
}

/**
 * The primary cell — actor and action, with the action's token rendered as written.
 *
 * Exported for the same reason {@link AuditDiff} is: it renders an unknown action
 * differently, and "unknown" is a state that only exists when the server is ahead of this
 * bundle. A test cannot produce that by deploying; it can produce it by calling this.
 */
export function AuditActionCell({ entry }: { entry: AuditLogEntry }) {
  const action = describeAction(entry.action);
  return (
    <>
      {/*
        The phrase reads; the token is carried on `title` so a support conversation can
        still name the exact string the write path recorded. `screens.md` §13 wants a
        plain-English verb phrase and not an event code — this file previously rendered
        the token as written, and that decision is reversed rather than quietly dropped.
      */}
      <span
        className="pp-audit-action"
        data-known={action.known ? "true" : "false"}
        data-action={action.token}
        title={action.token}
      >
        {action.phrase}
      </span>
      {action.known ? null : (
        <span className="pp-audit-unknown"> — recorded by a newer version</span>
      )}
    </>
  );
}

export function AuditLog() {
  const [expanded, setExpanded] = useState<string | null>(null);

  /**
   * The keyboard cursor, and it is not decoration — **without it this screen's diff could
   * not be opened at all.**
   *
   * `DataTable` calls `onOpen` from exactly one place, its `onKeyDown`, and gates both
   * that handler and the grid's `tabIndex` on `cursor !== undefined`. From c36 until c37c
   * this screen passed `onOpen` and no `cursor`, so the grid was not focusable, the
   * handler did not exist, and `onOpen` was unreachable. Measured in a browser rather than
   * argued: the grid's `tabindex` attribute was `null`, fifteen `Tab` presses never
   * reached it, and calling `.focus()` on it directly returned `false`.
   *
   * The pairing is now a type error (`KeyboardProps` in `DataTable.tsx`), so this exact
   * shape cannot ship again.
   */
  const [cursor, setCursor] = useState<TableCursor>(INITIAL_CURSOR);

  const list = useInfiniteQuery({
    queryKey: ["auditLog", "list"],
    queryFn: ({ pageParam }) =>
      api.auditLog({
        limit: DEFAULT_AUDIT_PAGE_SIZE,
        ...(typeof pageParam === "string" ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    // `null` and absent both mean "last page" — the API says so, and treating them
    // differently here would be a second reading of one contract.
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });

  const entries = useMemo(
    () => (list.data?.pages ?? []).flatMap((page) => page.entries),
    [list.data],
  );
  const rows: Row[] = useMemo(
    () => entries.map((entry) => ({ id: entry.id, entry })),
    [entries],
  );

  const toggle = useCallback(
    (id: string) => setExpanded((current) => (current === id ? null : id)),
    [],
  );

  const columns: ReadonlyArray<Column<Row>> = useMemo(
    () => [
      /*
       * WHEN · WHO · WHAT · WHERE, ruled by `screens.md` §13 (2026-08-11).
       *
       * This read Action · Who · What(entity) · When, which put the event code first and
       * the time last. The ruled order is the order an auditor reads a row in — when did
       * it happen, who did it, what did they do, what did they do it to — and it is why
       * `WHAT` now carries a verb phrase rather than a token.
       *
       * `primary` stays on WHAT rather than moving to the first column: it is a flag in
       * `DataTable`, not a position, and the primary cell is the one that carries the
       * row's open affordance and absorbs folded columns below the fold. The thing a
       * narrow screen must keep is what happened, not when.
       */
      {
        key: "at",
        header: "WHEN",
        width: 200,
        foldsNarrow: true,
        render: (row) => (
          // The machine-readable value stays in the DOM beside the rendered one: this is
          // the column a regulator or a support conversation quotes, and a formatted string
          // is lossy about the timezone it was formatted in.
          <time dateTime={row.entry.at}>{auditTimestamp(row.entry.at)}</time>
        ),
      },
      {
        key: "actor",
        header: "WHO",
        width: 220,
        render: (row) => actorLabel(row.entry.actor),
      },
      {
        key: "action",
        header: "WHAT",
        width: 260,
        primary: true,
        /**
         * The mouse affordance, which `DataTable` requires the screen to supply and
         * which this screen did not supply at all until c37c.
         *
         * **A button, not a link, and the choice follows what opening does.** The table's
         * contract says a navigating open is a `<Link>` — it should carry an href a user
         * can middle-click, copy and read in the status bar. This open navigates nowhere:
         * it discloses the before/after in place, from `expanded` state on this screen. A
         * link pointing at nothing would have worse semantics than a button, not better,
         * and the correct element for "reveal detail below" is a button carrying
         * `aria-expanded`.
         *
         * `aria-expanded` is the part a sighted mouse user never sees and a screen-reader
         * user depends on: without it the control announces as an ordinary button and the
         * detail appearing elsewhere in the document is unannounced.
         */
        render: (row) => (
          <button
            type="button"
            className="pp-audit-open"
            aria-expanded={expanded === row.id}
            onClick={() => toggle(row.id)}
          >
            <AuditActionCell entry={row.entry} />
          </button>
        ),
      },
      {
        key: "entity",
        header: "WHERE",
        width: 200,
        foldsNarrow: true,
        render: (row) => (
          <span className="pp-audit-entity">
            {row.entry.entity}
            {row.entry.entityId === null ? null : (
              <span className="pp-audit-entity-id"> {row.entry.entityId}</span>
            )}
          </span>
        ),
      },
    ],
    // `expanded` is here because the primary column now renders `aria-expanded` from it.
    // Left as `[]` the columns would be memoised against the first render's value and the
    // attribute would never change — a control that says "collapsed" while the detail is
    // open, which is worse than no attribute at all for the reader who depends on it.
    // `toggle` is a `useCallback` with no dependencies and is stable; it is named anyway
    // so the list says what it closes over rather than what happens to change.
    [expanded, toggle],
  );

  if (list.isPending) return <p>Loading the audit log…</p>;

  if (list.isError) {
    return (
      <Surface tone="warn" emphasis="filled">
        <p>The audit log could not be loaded. Nothing has been changed by this.</p>
      </Surface>
    );
  }

  if (entries.length === 0) {
    // An organization always has at least `org.create`, written by the bootstrap in the
    // same transaction as the organization itself — so an empty log means the read is
    // wrong, not that nothing has happened. Saying that is more useful than "no results".
    return (
      <Surface tone="neutral" emphasis="filled">
        <p>
          This log is empty, which is unexpected — every organization records its own
          creation. If you are seeing this, the trail is not being read correctly rather
          than being genuinely empty.
        </p>
      </Surface>
    );
  }

  return (
    <section className="pp-audit-log">
      <h1>Audit log</h1>
      <p className="pp-audit-intro">
        Every change made in this organization, newest first. The trail cannot be edited
        or deleted — by anyone, including us.
      </p>

      <DataTable
        columns={columns}
        rows={rows}
        caption="Audit log"
        onOpen={toggle}
        cursor={cursor}
        onCursorChange={setCursor}
      />

      {expanded === null ? null : (
        <div className="pp-audit-detail">
          <h2>Details</h2>
          <AuditDiff
            before={entries.find((e) => e.id === expanded)?.before}
            after={entries.find((e) => e.id === expanded)?.after}
          />
          <button type="button" onClick={() => setExpanded(null)}>
            Close
          </button>
        </div>
      )}

      {list.hasNextPage ? (
        <button
          type="button"
          onClick={() => void list.fetchNextPage()}
          disabled={list.isFetchingNextPage}
        >
          {list.isFetchingNextPage ? "Loading…" : "Load older entries"}
        </button>
      ) : null}
    </section>
  );
}
